import {DocumentVideoIcon} from '@sanity/icons/DocumentVideo'
import {PlugIcon} from '@sanity/icons/Plug'
import {SearchIcon} from '@sanity/icons/Search'
import {UploadIcon} from '@sanity/icons/Upload'
import {Button, Card, Flex, Inline, Text} from '@sanity/ui'
import {useCallback} from 'react'

import {useAccessControl} from '../hooks/useAccessControl'
import type {SetDialogState} from '../hooks/useDialogState'
import {type PluginConfig} from '../util/types'
import {FileInputButton, type FileInputButtonProps} from './FileInputButton'

function formatAcceptString(accept: string): string {
  return accept
    .split(',')
    .map((type) => type.trim().replace('/*', ''))
    .join(' or ')
}

interface UploadPlaceholderProps {
  setDialogState: SetDialogState
  readOnly: boolean
  hovering: boolean
  needsSetup: boolean
  onSelect: FileInputButtonProps['onSelect']
  config: PluginConfig
  accept: string
}
export default function UploadPlaceholder(props: UploadPlaceholderProps) {
  const {setDialogState, readOnly, onSelect, hovering, needsSetup, accept} = props
  const handleBrowse = useCallback(() => setDialogState('select-video'), [setDialogState])
  const handleConfigureApi = useCallback(() => setDialogState('secrets'), [setDialogState])
  const {hasConfigAccess} = useAccessControl(props.config)

  return (
    <Card
      sizing="border"
      tone={readOnly ? 'transparent' : 'inherit'}
      border
      radius={2}
      paddingX={3}
      paddingY={1}
      style={hovering ? {borderColor: 'transparent'} : undefined}
    >
      <Flex
        align="center"
        justify="space-between"
        gap={4}
        direction={['column', 'column', 'row']}
        paddingY={2}
        sizing="border"
      >
        <Flex align="center" justify="flex-start" gap={2} flex={1}>
          <Flex justify="center">
            <Text muted>
              <DocumentVideoIcon />
            </Text>
          </Flex>
          <Flex justify="center">
            <Text size={1} muted>
              Drag {formatAcceptString(accept)} file or paste URL here
            </Text>
          </Flex>
        </Flex>
        <Inline gap={2}>
          <FileInputButton
            accept={accept}
            mode="bleed"
            tone="default"
            icon={UploadIcon}
            text="Upload"
            onSelect={onSelect}
          />
          <Button mode="bleed" icon={SearchIcon} text="Select" onClick={handleBrowse} />

          {hasConfigAccess && (
            <Button
              padding={3}
              radius={3}
              tone={needsSetup ? 'critical' : undefined}
              onClick={handleConfigureApi}
              icon={PlugIcon}
              mode="bleed"
              title="Configure plugin credentials"
            />
          )}
        </Inline>
      </Flex>
    </Card>
  )
}
